import { Commit } from "components/icons/generated";
import { StackSuggestions } from "constants/stack";
import { Stack } from "types/generated";
import { getCommitLabel } from "utils/commit";

import MetaInfoListItem from "./Item";
import styles from "./styles.module.css";

type MetaInfoBlobStackCommitProps = {
  stack: Stack;
  handleApplyFilter?: (keyName: StackSuggestions) => (value: string) => void;
};

/**
 * The Blob Storage variant of MetaInfoStackCommit.
 *
 * Blob Storage reads source archives from an object store, so there is no commit page
 * to link to: the same information is rendered as plain text. "Outdated" here means the
 * HEAD pointer in the bucket names a different commit than the one the stack is pinned to.
 */
const MetaInfoBlobStackCommit = ({ stack, handleApplyFilter }: MetaInfoBlobStackCommitProps) => {
  const { provider, trackedCommit, trackedBranchHead, trackedCommitSetBy } = stack;

  if (!trackedCommit) {
    return (
      <MetaInfoListItem icon={Commit} tooltip={trackedCommitSetBy}>
        HEAD
      </MetaInfoListItem>
    );
  }

  const trackedCommitIsOutdated =
    !!trackedBranchHead && trackedCommit.hash !== trackedBranchHead.hash;

  const commitLabel = getCommitLabel(provider, trackedCommit.hash);

  return (
    <div className={styles.trackedCommitWrapper}>
      <MetaInfoListItem
        applyFilter={handleApplyFilter && handleApplyFilter(StackSuggestions.Commit)}
        // No href, so MetaInfoListItem never renders a link. linkText labels the filter
        // dropdown, children the plain text shown when there is no dropdown at all.
        linkText={commitLabel}
        icon={Commit}
        tooltip={
          (trackedCommitSetBy || trackedCommitIsOutdated) && (
            <>
              {trackedCommitSetBy && `${trackedCommitSetBy}.`}
              {trackedCommitIsOutdated && (
                <span className={styles.outdatedMessage}>
                  Stack's tracked commit is not the one the HEAD pointer in blob storage names.
                </span>
              )}
            </>
          )
        }
        type="commit"
        tooltipWidthMode="maxWidthSm"
      >
        {commitLabel}
      </MetaInfoListItem>

      {trackedCommitIsOutdated && <span className={styles.outdatedIndicator}>&nbsp;</span>}
    </div>
  );
};

export default MetaInfoBlobStackCommit;
